import { useState, useRef, useCallback } from 'react';

/**
 * Tracks the currently hovered shape/connector/point for LIVE display
 * (toolbar hover-row) — same event-delegation pattern as
 * UmlCanvas.useShapeDeleteShortcut.js (event.target.closest(...) on the world
 * div), but with real React state instead of a ref, since this needs to
 * actually re-render something on every hover change, not just be read on a
 * keypress.
 *
 * All three are computed independently (not mutually exclusive) — hovering a
 * waypoint/anchor sets BOTH hoveredGeo (exact point) AND hoveredConnectorId
 * (its parent connector, data-connector-id lives on the same element), so the
 * toolbar can show both together.
 *
 * Dedupes against the PREVIOUS hover target (nextKey) so mouse jitter within
 * the same element doesn't churn state on every mouseover bubble event.
 * Debug-logging to OutputMsgWidget happens in UmlCanvas.jsx instead of here —
 * that's where getShapeById/connectors (needed to resolve readable names) live.
 * @returns {{hoveredShapeId: string|null, hoveredConnectorId: string|null, hoveredGeo: {label:string,x:number,y:number}|null, handleWorldMouseOverForDisplay: Function}}
 */
export default function useHoveredShapeId() {
    const [hoveredShapeId, setHoveredShapeId] = useState(null);
    const [hoveredConnectorId, setHoveredConnectorId] = useState(null);
    const [hoveredGeo, setHoveredGeo] = useState(null);
    const lastKeyRef = useRef(null);

    const handleWorldMouseOverForDisplay = useCallback(function trackHoveredForDisplay(event) {
        const shapeEl = event.target.closest('[data-shape-id]');
        const nextShapeId = shapeEl ? shapeEl.getAttribute('data-shape-id') : null;

        const connectorEl = event.target.closest('[data-connector-id]');
        const nextConnectorId = connectorEl ? connectorEl.getAttribute('data-connector-id') : null;

        const geoEl = event.target.closest('[data-geo-x]');
        const nextGeo = geoEl
            ? {
                  label: geoEl.getAttribute('data-geo-label'),
                  x: Number(geoEl.getAttribute('data-geo-x')),
                  y: Number(geoEl.getAttribute('data-geo-y')),
              }
            : null;

        // Dedupe key: identifies WHAT is hovered, not the exact pixel — avoids
        // state churn on mouse jitter within the same element.
        const nextKey = nextGeo ? `geo:${nextGeo.label}:${nextConnectorId}` : nextConnectorId ? `conn:${nextConnectorId}` : nextShapeId ? `shape:${nextShapeId}` : null;
        if (nextKey === lastKeyRef.current) return;
        lastKeyRef.current = nextKey;

        setHoveredShapeId(nextShapeId);
        setHoveredConnectorId(nextConnectorId);
        setHoveredGeo(nextGeo);
    }, []);

    return { hoveredShapeId, hoveredConnectorId, hoveredGeo, handleWorldMouseOverForDisplay };
}
